import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

// The Stream Ingest page's heading, with the one-line subtitle the Cost page
// shows under each section rather than behind its info icon.
export const SectionHeading = ({ title, help, subtitle, children }) => (
  <Box sx={{ mb: 1.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Typography variant="subtitle1" fontWeight={700}>
        {title}
      </Typography>
      {help && (
        <Tooltip title={help} placement="right">
          <InfoOutlinedIcon
            sx={{ fontSize: 16, color: 'text.secondary', cursor: 'default' }}
          />
        </Tooltip>
      )}
      {children && <Box sx={{ flex: 1 }} />}
      {children}
    </Box>
    {subtitle && (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 0.5, maxWidth: 820 }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
)

// The Stream Ingest page's tile, with a line and a smaller line under the value.
export const Tile = ({ label, value, help, tone, note, sub }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, flex: '1 1 210px' }}>
    <Tooltip title={help || ''} placement="top">
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ cursor: 'default' }}
      >
        {label}
      </Typography>
    </Tooltip>
    <Typography
      variant="h5"
      fontWeight={700}
      color={tone || 'text.primary'}
      sx={{ mt: 0.5 }}
    >
      {value}
    </Typography>
    {note && (
      <Typography variant="body2" color="text.secondary">
        {note}
      </Typography>
    )}
    {sub && (
      <Typography
        variant="caption"
        color="text.secondary"
        component="div"
        sx={{ mt: 0.5 }}
      >
        {sub}
      </Typography>
    )}
  </Paper>
)

// A unit-cost figure inside a section, filled rather than outlined; two
// across on a phone.
export const Figure = ({ value, label, note }) => (
  <Box
    sx={{
      flex: { mobile: '1 1 130px', desktop: '1 1 170px' },
      minWidth: 0,
      p: 1.75,
      borderRadius: 2,
      bgcolor: 'action.hover',
    }}
  >
    <Typography variant="h5" fontWeight={700}>
      {value}
    </Typography>
    <Typography variant="body2" fontWeight={500}>
      {label}
    </Typography>
    {note && (
      <Typography variant="caption" color="text.secondary" component="div">
        {note}
      </Typography>
    )}
  </Box>
)

// Identity is never color alone: every chart here carries one of these.
export const Legend = ({ items }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1, fontSize: 12 }}>
    {items.map(({ key, label, color, dashed, outline }) => (
      <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box
          sx={{
            width: dashed ? 14 : 10,
            height: dashed ? 0 : 10,
            borderRadius: dashed ? 0 : '3px',
            bgcolor: dashed || outline ? 'transparent' : color,
            borderTop: dashed ? `2px dashed ${color}` : undefined,
            border: outline ? `1.5px dashed ${color}` : undefined,
            boxSizing: 'border-box',
          }}
        />
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Box>
    ))}
  </Box>
)

// The mockup's tables: a hairline box, a muted header row, scrolling sideways
// on a narrow screen rather than squeezing. On a phone each cell keeps to one
// line, so a row reads across; a cell spanning the row, an empty state,
// still wraps.
export const tableBox = {
  border: 1,
  borderColor: 'divider',
  borderRadius: 2,
  overflowX: 'auto',
}

export const tableHead = (theme) => ({
  '& th': {
    bgcolor: 'action.hover',
    color: 'text.secondary',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  [theme.breakpoints.down('desktop')]: {
    '& td:not([colspan])': { whiteSpace: 'nowrap' },
  },
})
